import React, { useRef, useState, useEffect } from "react";
import { motion, useAnimation, useInView, AnimatePresence, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Zap, Share2, Award, ChevronDown, Building2, Wallet, FileCheck, Lock, Heart, Activity, Sparkles, CheckCircle2, Play, Trophy, Medal, Video, Tv } from "lucide-react";
import { useTranslation } from "react-i18next";
import fa from "./locales/BrandIntroSection-fa.json";
import en from "./locales/BrandIntroSection-en.json";
import ar from "./locales/BrandIntroSection-ar.json";
import tr from "./locales/BrandIntroSection-tr.json";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const imgBadgeIcon1 = `<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="26" height="26" rx="13" fill="white" fillOpacity="0.1"/>
<rect x="4" y="4" width="18" height="18" rx="9" fill="white" fillOpacity="0.16"/>
<circle cx="13" cy="13" r="5" fill="white"/>
</svg>`;

const imgWomenHealthIcon = <Heart color="url(#iconGradient)" fill="url(#iconGradient)" />;
const imgSportIcon = <Activity color="url(#iconGradient)" fill="url(#iconGradient)" />;
const imgAIIcon = <Sparkles color="url(#iconGradient)" fill="url(#iconGradient)" />;

interface FeatureCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
}

// ─── Floating Particle ──────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ delay: number; x: number; color: string }> = ({ delay, x, color }) => (
    <motion.div
        className="absolute w-1.5 h-1.5 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: 0, backgroundColor: color }}
        initial={{ opacity: 0, y: 0, scale: 0 }}
        animate={{ opacity: [0, 0.8, 0], y: -40, scale: [0, 1, 0] }}
        transition={{ duration: 1.4, delay, ease: "easeOut" }}
    />
);

// ─── Tilt Card Wrapper ───────────────────────────────────────────────────────
const TiltCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => {
    const ref = useRef<HTMLDivElement>(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useTransform(y, [-0.5, 0.5], [4, -4]);
    const rotateY = useTransform(x, [-0.5, 0.5], [-4, 4]);
    const springConfig = { stiffness: 200, damping: 20 };
    const springRotateX = useSpring(rotateX, springConfig);
    const springRotateY = useSpring(rotateY, springConfig);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            className={className}
            style={{ rotateX: springRotateX, rotateY: springRotateY, transformStyle: "preserve-3d" }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {children}
        </motion.div>
    );
};

// ─── Pulse Ring ─────────────────────────────────────────────────────────────
const PulseRing: React.FC<{ color?: string }> = ({ color = "rgba(0,114,206,0.3)" }) => (
    <>
        {[0, 0.4, 0.8].map((delay) => (
            <motion.div
                key={delay}
                className="absolute inset-0 rounded-full"
                style={{ border: `1.5px solid ${color}` }}
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: 0, scale: 1.8 }}
                transition={{ duration: 2, delay, repeat: Infinity, ease: "easeOut" }}
            />
        ))}
    </>
);

// ─── Accordion Sub-item Component ───────────────────────────────────────────
const AccordionSubItem: React.FC<{ item: any; index: number }> = ({ item, index }) => {
    const [open, setOpen] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number; delay: number }[]>([]);
    const particleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerParticles = () => {
        const newParticles = Array.from({ length: 5 }, (_, i) => ({
            id: Date.now() + i,
            x: 10 + Math.random() * 80,
            delay: i * 0.1,
        }));
        setParticles(newParticles);
        particleTimerRef.current = setTimeout(() => setParticles([]), 1600);
    };

    useEffect(
        () => () => {
            if (particleTimerRef.current) clearTimeout(particleTimerRef.current);
        },
        []
    );

    const handleToggle = () => {
        setOpen((p) => !p);
        triggerParticles();
    };

    return (
        <motion.div
            className="relative rounded-2xl overflow-hidden"
            style={{
                background: open ? `linear-gradient(135deg, ${item.color}12 0%, rgba(255,255,255,0.6) 100%)` : "rgba(255,255,255,0.5)",
                border: `1px solid ${open ? item.color + "40" : "rgba(255,255,255,0.8)"}`,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.06, duration: 0.35 }}
            whileHover={{ scale: 1.005 }}
        >
            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} delay={p.delay} x={p.x} color={item.color} />
                ))}
            </div>

            <button className="w-full flex flex-row-reverse items-center justify-between gap-3 px-4 py-3 text-right cursor-pointer group" onClick={handleToggle} aria-expanded={open}>
                <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="flex-shrink-0">
                    <ChevronDown className="w-4 h-4 transition-colors duration-200" style={{ color: open ? item.color : "#9ca3af" }} />
                </motion.div>

                <div className="flex items-center gap-2.5 ">
                    <motion.div
                        className="w-7 h-7 rounded-full flex  items-center justify-center flex-shrink-0 transition-all duration-300"
                        style={{
                            backgroundColor: open ? item.color + "20" : "#f9fafb",
                            color: open ? item.color : "#9ca3af",
                        }}
                        animate={{ rotate: open ? 360 : 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {item.icon}
                    </motion.div>
                    <span className="text-[13px] font-bold text-gray-800 group-hover:text-gray-900 transition-colors">{item.title}</span>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 pt-2" style={{ borderTop: `1px solid ${item.color}20` }}>
                            <ul className="flex flex-col gap-2">
                                {item.items.map((subItem: any, i: number) => (
                                    <motion.li
                                        key={i}
                                        className="flex items-start gap-2.5 text-right"
                                        initial={{ opacity: 0, x: 10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.07, duration: 0.3 }}
                                    >
                                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.07 + 0.1, type: "spring", stiffness: 400 }}>
                                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: item.color }} />
                                        </motion.div>
                                        <p className="text-[12.5px] text-gray-600 leading-relaxed">
                                            <span className="font-semibold text-gray-800">{subItem.label}: </span>
                                            {subItem.desc}
                                        </p>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// ─── Operator Accordion Card ─────────────────────────────────────────────────
const OperatorAccordionCard: React.FC = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const accordionSubItems = [
        {
            icon: <Building2 className="w-4 h-4" strokeWidth={1.5} />,
            title: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.title"),
            color: "#0072CE",
            gradient: "from-blue-500/10 to-blue-500/5",
            items: [
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.noc.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.noc.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.soc.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.soc.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.contactCenter.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.infrastructure.contactCenter.desc"),
                },
            ],
        },
        {
            icon: <Wallet className="w-4 h-4" strokeWidth={1.5} />,
            title: t("brandIntro:BrandIntroSection.operatorCard.accordion.financial.title"),
            color: "#22c55e",
            gradient: "from-green-500/10 to-green-500/5",
            items: [
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.financial.capital.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.financial.capital.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.financial.development.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.financial.development.desc"),
                },
            ],
        },
        {
            icon: <FileCheck className="w-4 h-4" strokeWidth={1.5} />,
            title: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.title"),
            color: "#3b82f6",
            gradient: "from-blue-500/10 to-blue-500/5",
            items: [
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.ehr.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.ehr.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.medicine30.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.medicine30.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.tiering.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.regulatory.tiering.desc"),
                },
            ],
        },
        {
            icon: <Lock className="w-4 h-4" strokeWidth={1.5} />,
            title: t("brandIntro:BrandIntroSection.operatorCard.accordion.security.title"),
            color: "#a855f7",
            gradient: "from-purple-500/10 to-purple-500/5",
            items: [
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.security.afta.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.security.afta.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.security.passiveDefense.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.security.passiveDefense.desc"),
                },
            ],
        },
        {
            icon: <Zap className="w-4 h-4" strokeWidth={1.5} />,
            title: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.title"),
            color: "#eab308",
            gradient: "from-yellow-500/10 to-yellow-500/5",
            items: [
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.offline.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.offline.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.mci.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.mci.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.latency.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.network.latency.desc"),
                },
            ],
        },
        {
            icon: <Share2 className="w-4 h-4" strokeWidth={1.5} />,
            title: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.title"),
            color: "#06b6d4",
            gradient: "from-cyan-500/10 to-cyan-500/5",
            items: [
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.sso.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.sso.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.ewano.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.ewano.desc"),
                },
                {
                    label: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.b2b.label"),
                    desc: t("brandIntro:BrandIntroSection.operatorCard.accordion.integration.b2b.desc"),
                },
            ],
        },
    ];

    return (
        <motion.div
            className="w-full rounded-3xl overflow-hidden relative"
            style={{
                background: isOpen
                    ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(239,246,255,0.9) 100%)"
                    : "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(239,246,255,0.7) 100%)",
                border: `1px solid ${isHovered || isOpen ? "rgba(0,114,206,0.35)" : "rgba(0,114,206,0.15)"}`,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                boxShadow: isOpen
                    ? "0 20px 60px -10px rgba(0,114,206,0.15), 0 4px 20px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)"
                    : "0 4px 20px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
            }}
            layout
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
        >
            {/* Top shimmer line */}
            <motion.div
                className="absolute top-0 left-0 right-0 h-[2px] rounded-t-3xl"
                style={{
                    background: "linear-gradient(90deg, transparent 0%, #0072CE 50%, transparent 100%)",
                }}
                animate={{ opacity: isOpen ? 1 : isHovered ? 0.6 : 0.3 }}
                transition={{ duration: 0.3 }}
            />

            {/* Header */}
            <button className="w-full flex flex-row-reverse items-center justify-between gap-4 px-5 py-4 cursor-pointer text-right" onClick={() => setIsOpen((p) => !p)} aria-expanded={isOpen}>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: isOpen ? "rgba(0,114,206,0.1)" : "rgba(249,250,251,1)" }}
                >
                    <ChevronDown className="w-4 h-4" style={{ color: isOpen ? "#0072CE" : "#9ca3af" }} />
                </motion.div>

                <div className="flex flex-row items-center gap-3">
                    {/* Animated star badge */}
                    <motion.div
                        className="relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, #0284c7 0%, #0072CE 100%)", boxShadow: "0 4px 12px rgba(0,114,206,0.35)" }}
                        animate={{ scale: isHovered ? 1.1 : 1, rotate: isOpen ? 360 : 0 }}
                        transition={{ scale: { duration: 0.2 }, rotate: { duration: 0.6, ease: "easeInOut" } }}
                    >
                        <div className="absolute inset-0 rounded-full">{isHovered && <PulseRing color="rgba(0,114,206,0.4)" />}</div>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                                fill="white"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </motion.div>

                    {/* Text */}
                    <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                            <p className="text-[15px] font-bold text-gray-900 leading-snug">{t("brandIntro:BrandIntroSection.operatorCard.title")}</p>
                        </div>
                        <p className="text-[11px] text-gray-600 font-medium leading-relaxed">{t("brandIntro:BrandIntroSection.operatorCard.subtitle")}</p>
                    </div>
                </div>
            </button>

            {/* Expandable Body */}
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-5 flex flex-col gap-3" style={{ borderTop: "1px solid rgba(0,114,206,0.1)" }}>
                            {/* Intro */}
                            <motion.div
                                className="mt-3 rounded-2xl p-3.5 text-right"
                                style={{
                                    background: "linear-gradient(135deg, rgba(0,114,206,0.06) 0%, rgba(255,255,255,0.4) 100%)",
                                    border: "1px solid rgba(0,114,206,0.12)",
                                }}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1, duration: 0.35 }}
                            >
                                <p className="text-[12.5px] text-gray-600 leading-[1.9]">
                                    {t("brandIntro:BrandIntroSection.operatorCard.intro.part1")}
                                    <span className="font-semibold text-gray-800 border-b border-blue-200">{t("brandIntro:BrandIntroSection.operatorCard.intro.highlight1")}</span>
                                    {t("brandIntro:BrandIntroSection.operatorCard.intro.part2")}
                                    <span className="font-semibold text-[#0072CE]">{t("brandIntro:BrandIntroSection.operatorCard.intro.highlight2")}</span>
                                    {t("brandIntro:BrandIntroSection.operatorCard.intro.part3")} <span className="font-semibold text-gray-800">{t("brandIntro:BrandIntroSection.operatorCard.intro.highlight3")}</span>
                                    {t("brandIntro:BrandIntroSection.operatorCard.intro.part4")}
                                </p>
                            </motion.div>

                            {/* Sub-items */}
                            <div className="flex flex-col gap-2">
                                {accordionSubItems.map((item, i) => (
                                    <AccordionSubItem key={i} item={item} index={i} />
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// ─── Feature Card ─────────────────────────────────────────────────────────────
const FeatureCard: React.FC<{ feature: FeatureCardProps; index: number; hovered: boolean; onHover: (v: boolean) => void }> = ({ feature, index, hovered, onHover }) => {
    const [particles, setParticles] = useState<{ id: number; x: number; delay: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const spawnParticles = () => {
        const ps = Array.from({ length: 4 }, (_, i) => ({ id: Date.now() + i, x: 15 + Math.random() * 70, delay: i * 0.12 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 1600);
    };
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    return (
        <motion.div
            className="relative flex-1 min-w-[120px] rounded-2xl p-3 sm:p-4 flex flex-row sm:flex-col items-center gap-3 sm:gap-2 cursor-pointer overflow-hidden text-right sm:text-center"
            style={{
                background: hovered ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(239,246,255,0.9) 100%)" : "rgba(255,255,255,0.7)",
                border: `1px solid ${hovered ? "rgba(0,114,206,0.25)" : "rgba(255,255,255,0.9)"}`,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: hovered ? "0 16px 40px -8px rgba(0,114,206,0.18), 0 2px 8px rgba(0,0,0,0.04)" : "0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
            }}
            variants={{
                hidden: { opacity: 0, y: 24, scale: 0.92 },
                visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: { delay: index * 0.12, duration: 0.5, type: "spring", stiffness: 200 },
                },
            }}
            whileHover={{ y: -4, scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onHoverStart={() => {
                onHover(true);
                spawnParticles();
            }}
            onHoverEnd={() => onHover(false)}
        >
            {/* Top shimmer */}
            <motion.div
                className="absolute top-0 left-0 right-0 h-[1.5px] rounded-t-2xl"
                style={{ background: "linear-gradient(90deg, transparent, #0072CE, transparent)" }}
                animate={{ opacity: hovered ? 1 : 0, scaleX: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} delay={p.delay} x={p.x} color="#0072CE" />
                ))}
            </div>

            {/* Icon */}
            <motion.div
                className="relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full shrink-0"
                style={{ background: hovered ? "rgba(0,114,206,0.1)" : "rgba(239,246,255,1)" }}
                animate={{ rotate: hovered ? [0, -8, 8, 0] : 0, scale: hovered ? 1.1 : 1 }}
                transition={{ duration: 0.4 }}
            >
                {hovered && (
                    <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ border: "1.5px solid rgba(0,114,206,0.4)" }}
                        initial={{ scale: 1, opacity: 0.6 }}
                        animate={{ scale: 1.6, opacity: 0 }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                    />
                )}
                {feature.icon}
            </motion.div>

            {/* Text details container */}
            <div className="flex flex-col flex-1 min-w-0">
                {/* Title */}
                <motion.p className="text-[13px] sm:text-[13.5px] font-bold text-right sm:text-center w-full truncate sm:whitespace-normal" animate={{ color: hovered ? "#0072CE" : "#1f2937" }} transition={{ duration: 0.2 }}>
                    {feature.title}
                </motion.p>

                {/* Description */}
                <motion.p className="text-[11px] sm:text-[11.5px] text-right sm:text-center leading-snug sm:leading-relaxed w-full" animate={{ color: hovered ? "#4b5563" : "#9ca3af" }} transition={{ duration: 0.2 }}>
                    {feature.description}
                </motion.p>
            </div>

            {/* Bottom glow */}
            <motion.div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[30px] w-[60%] rounded-full blur-xl"
                style={{ background: "radial-gradient(ellipse, rgba(0,114,206,0.25) 0%, transparent 70%)" }}
                animate={{ opacity: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />
        </motion.div>
    );
};

// ─── Main Section ─────────────────────────────────────────────────────────────
const BrandIntroSection: React.FC = () => {
    const { t, i18n } = useTranslation();
    const controls = useAnimation();
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
    const [isFlipped, setIsFlipped] = useState(false);
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);

    useEffect(() => {
        i18n.addResourceBundle("fa", "brandIntro", fa, true, true);
        i18n.addResourceBundle("en", "brandIntro", en, true, true);
        i18n.addResourceBundle("ar", "brandIntro", ar, true, true);
        i18n.addResourceBundle("tr", "brandIntro", tr, true, true);
    }, []);

    const imgVideoThumbnail = t("brandIntro:BrandIntroSection.media.videoThumbnailOrange");
    const imgVideoThumbnailBlue = t("brandIntro:BrandIntroSection.media.videoThumbnailBlue");
    const imgPlayButton = t("brandIntro:BrandIntroSection.media.playButton");
    const videoLinkOrange = t("brandIntro:BrandIntroSection.media.videoLinkOrange");
    const videoLinkBlue = t("brandIntro:BrandIntroSection.media.videoLinkBlue");
    const imgBadgeIcon2 = t("brandIntro:BrandIntroSection.media.badgeIcon2");

    useEffect(() => {
        if (isInView) controls.start("visible");
    }, [controls, isInView]);
    useEffect(() => {
        const id = setInterval(() => setIsFlipped((p) => !p), 4000);
        return () => clearInterval(id);
    }, []);

    const handleCardClick = () => {
        setIsFlipped((prev) => !prev);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
    };

    const features: FeatureCardProps[] = [
        {
            icon: <div className="relative w-[36px] h-[36px] flex items-center justify-center">{imgWomenHealthIcon}</div>,
            title: t("brandIntro:BrandIntroSection.features.womenHealth.title"),
            description: t("brandIntro:BrandIntroSection.features.womenHealth.description"),
        },
        {
            icon: <div className="relative w-[36px] h-[36px] flex items-center justify-center">{imgSportIcon}</div>,
            title: t("brandIntro:BrandIntroSection.features.sportMode.title"),
            description: t("brandIntro:BrandIntroSection.features.sportMode.description"),
        },
        {
            icon: <div className="relative w-[36px] h-[36px] flex items-center justify-center">{imgAIIcon}</div>,
            title: t("brandIntro:BrandIntroSection.features.aiAssistant.title"),
            description: t("brandIntro:BrandIntroSection.features.aiAssistant.description"),
        },
    ];

    return (
        <section dir="rtl" className="relative overflow-hidden py-16 lg:py-20" ref={sectionRef}>
            <style>{`
                .perspective-1000 { perspective: 1000px; }
                .preserve-3d { transform-style: preserve-3d; }
                .backface-hidden { backface-visibility: hidden; }
            `}</style>

            <svg width="0" height="0" style={{ position: "absolute", zIndex: -1 }}>
                <defs>
                    <linearGradient id="iconGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                        <stop offset="0%" stopColor="#0072CE" />
                        <stop offset="100%" stopColor="#38bdf8" />
                    </linearGradient>
                </defs>
            </svg>

            {/* ── Background blobs ── */}
            <motion.div
                className="absolute top-20 left-10 w-96 h-96 bg-blue-100 rounded-full opacity-20 blur-3xl pointer-events-none"
                animate={{ scale: [1, 1.3, 1.1, 1], opacity: [0.15, 0.3, 0.2, 0.15], x: [0, 20, -10, 0], y: [0, -15, 10, 0] }}
                transition={{ duration: 12, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
            />
            <motion.div
                className="absolute bottom-20 right-10 w-64 h-64 bg-sky-100 rounded-full opacity-15 blur-2xl pointer-events-none"
                animate={{ scale: [1, 1.2, 0.9, 1], opacity: [0.1, 0.25, 0.15, 0.1], x: [0, -15, 20, 0], y: [0, 10, -20, 0] }}
                transition={{ duration: 15, repeat: Infinity, repeatType: "reverse", ease: "easeInOut", delay: 2 }}
            />
            <motion.div
                className="absolute top-1/2 right-1/4 w-32 h-32 bg-gradient-to-r from-blue-200 to-sky-200 rounded-full opacity-10 blur-xl pointer-events-none"
                animate={{ rotate: [0, 360], scale: [1, 1.5, 1], opacity: [0.05, 0.2, 0.05] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            />

            <div className="container relative z-10 mx-auto">
                <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch" initial="hidden" animate={controls}>
                    {/* ── Right: Flip Cards ── */}
                    {/* Right Side - Video Player */}
                    <motion.div className="lg:col-span-5 flex flex-col cursor-pointer self-stretch h-full min-h-[360px] sm:min-h-[460px] md:min-h-[520px]" style={{ perspective: "1000px" }} onClick={handleCardClick}>
                        <div className="relative w-full shadow-2xl h-full min-h-[360px] sm:min-h-[460px] md:min-h-[520px] perspective-1000 flex-1 flex flex-col rounded-[32px] sm:rounded-[40px]">
                            {/* Blue Card (Front - DEFAULT: Seda va Sima) */}
                            <motion.div
                                style={{
                                    alignSelf: "stretch",
                                    borderRadius: "40px",
                                    borderTop: "6px solid #38bdf8",
                                    background: "linear-gradient(180deg, #0072CE 0%, #0038A8 100%)",
                                }}
                                className="absolute inset-0 backface-hidden rounded-[40px] flex flex-col justify-between overflow-hidden"
                                animate={{
                                    rotateY: isFlipped ? 180 : 0,
                                }}
                                transition={{ duration: 0.8, ease: "easeInOut" }}
                            >
                                <div className="relative h-full flex flex-col justify-between">
                                    <div className="relative overflow-hidden p-2 md:p-0 flex-1 flex items-center justify-center">
                                        <motion.img
                                            src={imgVideoThumbnailBlue}
                                            alt={t("brandIntro:BrandIntroSection.videos.blue.thumbnailAlt")}
                                            className="w-full h-full object-cover max-h-[480px] mx-auto rounded-t-[34px] transition-transform duration-500"
                                            whileHover={{ scale: 1.02 }}
                                            animate={{ opacity: [0.9, 1, 0.9] }}
                                            transition={{
                                                opacity: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                                            }}
                                        />
                                        {/* Multi-layered minimal circular play button */}
                                        <motion.button
                                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group z-20 cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(videoLinkBlue, "_blank");
                                            }}
                                            whileHover={{ scale: 1.08 }}
                                            whileTap={{ scale: 0.95 }}
                                            aria-label={t("brandIntro:BrandIntroSection.videos.playAriaLabel")}
                                        >
                                            <div className="absolute -inset-4 rounded-full bg-white/20 backdrop-blur-md animate-pulse border border-white/30 shadow-lg" />
                                            <div className="absolute -inset-2 rounded-full bg-white/35 backdrop-blur-lg border border-white/50 shadow-md group-hover:scale-105 transition-transform duration-300" />
                                            <div className="relative w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.3)] border border-white flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:shadow-[0_15px_35px_rgba(0,0,0,0.4)]">
                                                <Play className="w-7 h-7 sm:w-8 sm:h-8 text-[#0072CE] fill-[#0072CE] ml-1 group-hover:scale-110 transition-transform duration-300" />
                                            </div>
                                        </motion.button>
                                    </div>
                                    <div className="relative bottom-0 left-0 right-0 h-16 cursor-pointer shrink-0" onClick={handleCardClick}>
                                        <div className="bg-[#0038A8] rounded-bl-[40px] rounded-br-[40px] px-6 sm:px-8 flex items-center justify-center gap-3 h-full border-t border-white/10">
                                            <div className="relative flex items-center justify-center w-8 h-8 shrink-0">
                                                <motion.div
                                                    className="absolute inset-0 rounded-full bg-white/40"
                                                    animate={{ scale: [1, 1.45, 1], opacity: [0.7, 0, 0.7] }}
                                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                                />
                                                <div className="relative w-8 h-8 rounded-full bg-white/20 backdrop-blur-md border border-white/40 flex items-center justify-center shadow-xs">
                                                    <Tv className="w-4 h-4 text-white" />
                                                </div>
                                            </div>
                                            <motion.p className="text-white text-right text-xs sm:text-sm md:text-base font-bold truncate" animate={{ opacity: [0.85, 1, 0.85] }} transition={{ duration: 3, repeat: Infinity }}>
                                                {t("brandIntro:BrandIntroSection.videos.blue.captionPrefix")}
                                                <span className="font-medium"> {t("brandIntro:BrandIntroSection.videos.blue.captionHighlight")}</span>
                                            </motion.p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Orange Gradient Card (Back: Zoomit) */}
                            <motion.div
                                style={{
                                    alignSelf: "stretch",
                                    borderRadius: "40px",
                                    borderTop: "6px solid #FF8C00",
                                    background: "linear-gradient(180deg, #FF6B00 0%, #C43000 100%)",
                                    transform: "rotateY(180deg)",
                                }}
                                className="absolute inset-0 backface-hidden rounded-[40px] flex flex-col justify-between overflow-hidden"
                                animate={{
                                    rotateY: isFlipped ? 0 : -180,
                                }}
                                transition={{ duration: 0.8, ease: "easeInOut" }}
                            >
                                <div className="relative h-full flex flex-col justify-between">
                                    <div className="relative overflow-hidden p-2 md:p-0 flex-1 flex items-center justify-center">
                                        <motion.img
                                            src={imgVideoThumbnail}
                                            alt={t("brandIntro:BrandIntroSection.videos.orange.thumbnailAlt")}
                                            className="w-full h-full object-cover max-h-[480px] mx-auto rounded-t-[34px] transition-transform duration-500"
                                            whileHover={{ scale: 1.02 }}
                                            animate={{ opacity: [0.9, 1, 0.9] }}
                                            transition={{
                                                opacity: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                                            }}
                                        />
                                        {/* Multi-layered minimal circular play button */}
                                        <motion.button
                                            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group z-20 cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(videoLinkOrange, "_blank");
                                            }}
                                            whileHover={{ scale: 1.08 }}
                                            whileTap={{ scale: 0.95 }}
                                            aria-label={t("brandIntro:BrandIntroSection.videos.playAriaLabel")}
                                        >
                                            <div className="absolute -inset-4 rounded-full bg-white/20 backdrop-blur-md animate-pulse border border-white/30 shadow-lg" />
                                            <div className="absolute -inset-2 rounded-full bg-white/35 backdrop-blur-lg border border-white/50 shadow-md group-hover:scale-105 transition-transform duration-300" />
                                            <div className="relative w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-white/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.3)] border border-white flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:shadow-[0_15px_35px_rgba(0,0,0,0.4)]">
                                                <Play className="w-7 h-7 sm:w-8 sm:h-8 text-[#FF6B00] fill-[#FF6B00] ml-1 group-hover:scale-110 transition-transform duration-300" />
                                            </div>
                                        </motion.button>
                                    </div>
                                    <div className="relative bottom-0 left-0 right-0 h-16 cursor-pointer shrink-0" onClick={handleCardClick}>
                                        <div className="bg-[#C43000] rounded-bl-[40px] rounded-br-[40px] px-6 sm:px-8 flex items-center justify-center gap-3 h-full border-t border-white/10">
                                            <div className="relative flex items-center justify-center w-8 h-8 shrink-0">
                                                <motion.div
                                                    className="absolute inset-0 rounded-full bg-white/40"
                                                    animate={{ scale: [1, 1.45, 1], opacity: [0.7, 0, 0.7] }}
                                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                                />
                                                <div className="relative w-8 h-8 rounded-full bg-white/20 backdrop-blur-md border border-white/40 flex items-center justify-center shadow-xs">
                                                    <Video className="w-4 h-4 text-white" />
                                                </div>
                                            </div>
                                            <motion.p className="text-white text-right text-xs sm:text-sm md:text-base font-bold truncate" animate={{ opacity: [0.85, 1, 0.85] }} transition={{ duration: 3, repeat: Infinity }}>
                                                {t("brandIntro:BrandIntroSection.videos.orange.captionPrefix")}
                                                <span className="font-medium"> {t("brandIntro:BrandIntroSection.videos.orange.captionHighlight")}</span>
                                            </motion.p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                    {/* ── Left: Content ── */}
                    <motion.div className="lg:col-span-7 md:mr-3 lg:mr-6 flex flex-col space-y-7">
                        {/* Badges */}
                        <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-center gap-3 w-full">
                            {[
                                {
                                    content: (
                                        <>
                                            <motion.div
                                                className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/10 via-sky-500/10 to-blue-600/15 border border-blue-200/80 shadow-2xs flex items-center justify-center shrink-0 text-[#0072CE]"
                                                whileHover={{ rotate: [0, -8, 8, 0], scale: 1.05 }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <Trophy className="w-5 h-5 text-[#0072CE]" />
                                            </motion.div>
                                            <div className="text-right">
                                                <p className="text-[11px] text-gray-600 font-semibold mb-0.5">{t("brandIntro:BrandIntroSection.badges.badge1.subtitle")}</p>
                                                <p className="text-[13px] text-gray-900 font-bold">{t("brandIntro:BrandIntroSection.badges.badge1.title")}</p>
                                            </div>
                                        </>
                                    ),
                                    hoverBorder: "rgba(0,114,206,0.3)",
                                    delay: 0.3,
                                },
                                {
                                    content: (
                                        <>
                                            <motion.div
                                                className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/10 via-sky-500/10 to-blue-600/15 border border-blue-200/80 shadow-2xs flex items-center justify-center shrink-0 text-[#0072CE]"
                                                whileHover={{ rotate: [0, 8, -8, 0], scale: 1.05 }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <Award className="w-5 h-5 text-[#0072CE]" />
                                            </motion.div>
                                            <div className="text-right">
                                                <p className="text-[11px] text-gray-600 font-semibold mb-0.5">{t("brandIntro:BrandIntroSection.badges.badge2.subtitle")}</p>
                                                <p className="text-[13px] text-gray-900 font-bold">{t("brandIntro:BrandIntroSection.badges.badge2.title")}</p>
                                            </div>
                                        </>
                                    ),
                                    hoverBorder: "rgba(0,114,206,0.3)",
                                    delay: 0.45,
                                },
                            ].map((badge, i) => (
                                <motion.div
                                    key={i}
                                    className="rounded-2xl p-2.5 pl-4 pr-3 flex items-center w-full md:w-auto justify-start gap-3 cursor-pointer"
                                    style={{
                                        background: "rgba(255,255,255,0.7)",
                                        border: "1px solid rgba(226, 232, 240, 0.8)",
                                        backdropFilter: "blur(12px)",
                                        WebkitBackdropFilter: "blur(12px)",
                                    }}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: badge.delay, duration: 0.6 }}
                                    whileHover={{ scale: 1.02, boxShadow: `0 8px 24px rgba(0,0,0,0.06)`, borderColor: badge.hoverBorder }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {badge.content}
                                </motion.div>
                            ))}
                        </div>

                        {/* Headings */}
                        <motion.div className="flex flex-col gap-4" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
                            <div className="flex flex-col gap-0">
                                <h2 className="text-3xl md:text-4xl lg:text-[36px] font-bold text-[#0C0E15] leading-[1.8] tracking-[-0.32px] text-right">
                                    <span className="font-bold">{t("brandIntro:BrandIntroSection.headings.h1.bold")} </span>
                                    <span className="font-normal">{t("brandIntro:BrandIntroSection.headings.h1.normal")}</span>
                                </h2>
                                <h3 className="text-3xl md:text-4xl lg:text-[36px] font-bold text-[#0C0E15] leading-[1.8] tracking-[-0.72px] text-right">
                                    <span className="font-medium">{t("brandIntro:BrandIntroSection.headings.h2.part1")}</span>
                                    <span> {t("brandIntro:BrandIntroSection.headings.h2.part2")} </span>
                                    <motion.span
                                        className="font-bold"
                                        style={{ background: "linear-gradient(281deg, #0072CE 1.84%, #0284c7 85.82%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                                        whileHover={{ scale: 1.04 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        {t("brandIntro:BrandIntroSection.headings.h2.highlight")}
                                    </motion.span>
                                </h3>
                            </div>
                            <motion.p
                                className="text-base text-[#2F3952] leading-[1.9] tracking-[-0.32px] text-right"
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                            >
                                {t("brandIntro:BrandIntroSection.description")}
                            </motion.p>
                        </motion.div>

                        {/* Feature Cards */}
                        <motion.div className="flex flex-col sm:flex-row w-full gap-2.5 sm:gap-3" variants={containerVariants} initial="hidden" animate="visible">
                            {features.map((feature, index) => (
                                <FeatureCard key={index} feature={feature} index={index} hovered={hoveredCard === index} onHover={(v) => setHoveredCard(v ? index : null)} />
                            ))}
                        </motion.div>

                        {/* Operator Accordion */}
                        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }}>
                            <OperatorAccordionCard />
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default BrandIntroSection;
